/**
 * Type utility functions for dev-tools
 * Part of Phase 1 refactoring - Foundation (Types & Utilities)
 *
 * These are pure functions for type checking and value conversion
 */
import { FieldType } from "../types";

/**
 * Checks if a string is a valid FieldType
 */
export const isValidFieldType = (type: string): type is FieldType => {
  const validTypes: FieldType[] = [
    "string",
    "number",
    "boolean",
    "enum",
    "message",
    "repeated",
    "bytes",
    "int32",
    "uint32",
    "float",
  ];
  return validTypes.includes(type as FieldType);
};

/**
 * Formats a field value for display based on its type
 */
export const formatFieldValue = (value: unknown, type: FieldType): string => {
  if (value === null || value === undefined) return "";

  switch (type) {
    case "boolean":
      return String(Boolean(value));
    case "number":
    case "int32":
    case "uint32":
    case "float":
      return String(Number(value));
    case "string":
      return String(value);
    case "enum":
      return String(value);
    case "repeated":
      return Array.isArray(value) ? JSON.stringify(value) : String(value);
    case "message":
      return typeof value === "object"
        ? JSON.stringify(value, null, 2)
        : String(value);
    case "bytes":
      return String(value);
    default:
      return JSON.stringify(value);
  }
};

/**
 * Parses a string value to the appropriate type
 */
export const parseFieldValue = (value: string, type: FieldType): unknown => {
  if (!value && value !== "0" && value !== "false") return undefined;

  switch (type) {
    case "boolean":
      return value === "true" || value === "1" || value === "on";
    case "number":
    case "float":
      const floatVal = parseFloat(value);
      return isNaN(floatVal) ? undefined : floatVal;
    case "int32":
    case "uint32":
      const intVal = parseInt(value, 10);
      return isNaN(intVal) ? undefined : intVal;
    case "string":
      return value;
    case "enum":
      const enumVal = parseInt(value, 10);
      return isNaN(enumVal) ? value : enumVal;
    case "repeated":
      try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed : [value];
      } catch {
        // If not valid JSON, treat as comma-separated values
        return value.split(",").map((item) => item.trim());
      }
    case "message":
      try {
        return JSON.parse(value);
      } catch {
        return value;
      }
    case "bytes":
      return value;
    default:
      try {
        return JSON.parse(value);
      } catch {
        return value;
      }
  }
};

/**
 * Validates if a value is appropriate for a given field type
 */
export const isValidValueForType = (
  value: unknown,
  type: FieldType
): boolean => {
  if (value === null || value === undefined) return true;

  switch (type) {
    case "boolean":
      return typeof value === "boolean";
    case "number":
    case "int32":
    case "uint32":
    case "float":
      return typeof value === "number" && !isNaN(value);
    case "string":
      return typeof value === "string";
    case "enum":
      return typeof value === "number" || typeof value === "string";
    case "repeated":
      return Array.isArray(value);
    case "message":
      return typeof value === "object";
    case "bytes":
      return typeof value === "string";
    default:
      return true;
  }
};

/**
 * Gets the default value for a field type
 */
export const getDefaultValueForType = (type: FieldType): unknown => {
  switch (type) {
    case "boolean":
      return false;
    case "number":
    case "int32":
    case "uint32":
    case "float":
      return 0;
    case "string":
    case "bytes":
      return "";
    case "enum":
      return 0;
    case "repeated":
      return [];
    case "message":
      return {};
    default:
      return undefined;
  }
};

/**
 * Checks if a field type requires special handling
 */
export const isComplexFieldType = (type: FieldType): boolean => {
  return ["message", "repeated"].includes(type);
};

/**
 * Determines if a field type is numeric
 */
export const isNumericFieldType = (type: FieldType): boolean => {
  return ["number", "int32", "uint32", "float"].includes(type);
};
